/**
 * ForceTracePage
 *
 * Full-page route component at /scoreboard/force.
 * Shows the force-time curve of the most recently completed test,
 * using the same settings/filters as the scoreboard.
 *
 * Designed to run in a separate browser tab/window alongside the scoreboard —
 * one monitor for rankings, another for the live force trace.
 */

import * as React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { ITest } from '../../../data/types';
import {
  testMatchesFilters,
  findLatestTest,
  buildScoreboardLeaderboard,
} from '../../../data/providers/utils/scoreboardFilters';
import { useEntities } from '../../../hooks/useEntities';
import { useGraphData } from '../../../hooks/useGraphData';
import { useScoreboardData } from '../../../hooks/useScoreboardData';
import { roundValue } from '../../../utils/math';
import { LinearLoading } from '../../shared/loading/LinearLoading';

import { ScoreboardThemeProvider } from './context/ScoreboardThemeContext';
import { ScoreboardContainer } from './components/ScoreboardContainer';
import { ScoreboardHeader } from './components/ScoreboardHeader';
import { AthleteBanner } from './components/AthleteBanner';
import { ForceTraceChart } from './components/ForceTraceChart';

export const ForceTracePage: React.FC = (): React.ReactElement => {
  const {
    tests,
    settings,
    loading,
    currentMetric,
    currentTestType,
    savedTheme,
    savedBranding,
    savedDisplay,
  } = useScoreboardData();

  const { groups } = useEntities();

  // Find the latest test matching current filters
  const latestTest = React.useMemo((): ITest | null => {
    const filtered = tests.filter((test: ITest): boolean =>
      testMatchesFilters(test, settings)
    );
    return findLatestTest(filtered, settings, currentMetric?.value);
  }, [
    tests.length,
    currentMetric,
    settings?.testType,
    settings?.tags?.length,
    JSON.stringify(settings?.filteredTeams),
    JSON.stringify(settings?.filteredGroups),
  ]);

  // Build leaderboard and derive rank from it
  const leaderboard = React.useMemo(
    (): ITest[] =>
      buildScoreboardLeaderboard(
        tests.filter((test: ITest): boolean =>
          testMatchesFilters(test, settings)
        ),
        settings,
        currentMetric?.value
      ),
    [tests, settings, currentMetric]
  );

  const rank = React.useMemo((): number => {
    if (!latestTest?.athlete?.id || leaderboard.length === 0) return 0;
    const index = leaderboard.findIndex(
      (t: ITest): boolean => t.athlete?.id === latestTest.athlete?.id
    );
    return index >= 0 ? index + 1 : 0;
  }, [latestTest, leaderboard]);

  // Resolve athlete's group name from filtered group or test's groups
  const groupName = React.useMemo((): string => {
    if (!latestTest) return '';
    const filteredGroupId = settings?.filteredGroups?.[0];
    if (filteredGroupId) {
      const group = groups.find((g) => g.id === filteredGroupId);
      return group?.name ?? '';
    }
    const testGroupId = latestTest.groups?.[0];
    if (testGroupId) {
      const group = groups.find((g) => g.id === testGroupId);
      return group?.name ?? '';
    }
    return '';
  }, [latestTest, settings?.filteredGroups, groups]);

  // Fetch force data for the latest test
  const {
    graphData,
    graphRaw,
    timingEvents,
    dataMin,
    loading: graphLoading,
  } = useGraphData(latestTest, !!latestTest);

  const metricValue = latestTest
    ? roundValue(
        latestTest[currentMetric?.value]?.value,
        latestTest[currentMetric?.value]?.roundingPrecision
      )
    : '';

  const metricUnits = latestTest
    ? latestTest[currentMetric?.value]?.units ?? ''
    : '';

  return (
    <ScoreboardThemeProvider
      initialTheme={savedTheme}
      initialBranding={savedBranding}
      initialDisplay={savedDisplay}
    >
      <ScoreboardContainer showSettingsButton={false}>
        {/* Close button */}
        <Box
          as='button'
          position='absolute'
          top='40px'
          right='64px'
          zIndex={10}
          px={3}
          py={1}
          fontSize='12px'
          fontWeight={600}
          color='var(--scoreboard-text-primary, #ffffff)'
          bg='rgba(255, 255, 255, 0.1)'
          border='1px solid rgba(255, 255, 255, 0.2)'
          borderRadius='md'
          cursor='pointer'
          transition='all 0.2s ease-in-out'
          _hover={{ bg: 'rgba(255, 255, 255, 0.2)' }}
          onClick={(): void => {
            window.close();
          }}
        >
          Close
        </Box>

        <ScoreboardHeader testTypeLabel={currentTestType?.label} />

        {loading ? (
          <Flex flex={1} align='center' justify='center'>
            <LinearLoading />
          </Flex>
        ) : !latestTest ? (
          <Flex flex={1} align='center' justify='center'>
            <Text
              fontSize='18px'
              color='var(--scoreboard-text-secondary, #b0b0b0)'
            >
              Waiting for test data...
            </Text>
          </Flex>
        ) : (
          <Flex flex={1} direction='column' overflow='hidden'>
            <AthleteBanner
              athleteName={latestTest.athlete?.name ?? ''}
              metricValue={metricValue}
              metricUnits={metricUnits}
              metricLabel={currentMetric?.label ?? ''}
              rank={rank}
              groupName={groupName}
            />
            <ForceTraceChart
              test={latestTest}
              graphData={graphData}
              graphRaw={graphRaw}
              timingEvents={timingEvents}
              dataMin={dataMin}
              loading={graphLoading}
            />
          </Flex>
        )}
      </ScoreboardContainer>
    </ScoreboardThemeProvider>
  );
};
